import type { BoxProps, MotionPropsFor } from '@hover/blueprint';
import {
  Box,
  IconButton,
  SimpleGrid,
  forwardRef,
  motion,
  useBreakpointValue,
} from '@hover/blueprint';
import { iUser } from '@hover/icons';
import { isDefined } from '@hover/utilities';
import React from 'react';
import { useHistory, useLocation } from 'react-router';

import { HomeownerAppMenuNames } from 'src/modules/homeownerApp/providers/HomeownerAppProvider';
import { Button, Logo } from 'src/shared/components';
import {
  TranslationNamespace,
  useCurrentPage,
  useTranslate,
} from 'src/shared/hooks';
import { Route, generatePath } from 'src/shared/route';
import { buttonPressed } from 'src/shared/telemetry';

import { nudge } from '../../helpers/styles';
import {
  useHomeownerAppContext,
  useMaxWidthQuery,
  useStickyDepth,
} from '../../hooks';
import { useHideInFullscreen } from '../FullscreenImage';

import { NavbarButton } from './NavbarButton';
import {
  desktopNavbarHeight,
  mobileNavbarHeight,
  useMobileNavbarStyle,
} from './constants';
import { useNavbarScrollBackground } from './useNavbarScroll';

const useNavbarMode = (loggedIn: boolean): 'light' | 'dark' => {
  const page = useCurrentPage();
  return useBreakpointValue({
    base:
      !loggedIn && page === Route.HOMEOWNER_APP_SAVED.page ? 'dark' : 'light',
    tablet:
      !loggedIn && page === Route.HOMEOWNER_APP_HOME.page ? 'light' : 'dark',
  });
};

export type NavItemSchema = {
  name: string;
  path: string;
  trackId: string;
};

export type NavBarName = (typeof navbarItems)[number]['name'];

export interface NavbarProps extends MotionPropsFor<BoxProps> {
  mode?: 'light' | 'dark';
  isSticky?: boolean;
}

const MotionBox = motion(Box);

const navbarItems = [
  {
    name: 'ideas',
    path: Route.HOMEOWNER_APP_HOME.path,
    trackId: 'ideas-nav-bar',
  },
  {
    name: 'projects',
    path: Route.HOMEOWNER_APP_PROJECTS_LIST.path,
    trackId: 'projects-nav-bar',
  },
  {
    name: 'saved',
    path: Route.HOMEOWNER_APP_SAVED.path,
    trackId: 'favorites-nav-bar',
  },
] as const satisfies readonly NavItemSchema[];

const Navbar = forwardRef<NavbarProps, 'nav'>(
  ({ isSticky = true, ...props }, ref) => {
    const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);

    const { loggedIn, setCurrentMenu, trackingData } = useHomeownerAppContext();

    const belowTablet = useMaxWidthQuery('tablet');
    const zIndex = useStickyDepth();
    const mode = useNavbarMode(loggedIn);

    const style = useNavbarScrollBackground();
    const mobileNavbarStyle = useMobileNavbarStyle({ isGlass: true, loggedIn });
    const hideInFullscreen = useHideInFullscreen();

    const history = useHistory();
    const { pathname } = useLocation();

    // Determine the currently selected path by finding the item with longest
    // path (most nested route) that matches the currrent `pathname`
    const [selectedPath] = navbarItems
      .map(item => ('path' in item ? item.path : null))
      .filter(isDefined)
      .filter(path => pathname.startsWith(path))
      .sort((a, b) => b.length - a.length);

    const handleClick = ({ path, name, trackId }: NavItemSchema) => {
      history.push(generatePath(path));

      const isDesktop = trackingData?.device_type === 'desktop';
      const navBarTrackingData = {
        ...trackingData,
        button_location: isDesktop ? 'top-nav-bar' : 'bottom-nav-bar',
        button_text: name,
        track_id: trackId,
      };

      buttonPressed(navBarTrackingData);
    };

    return (
      <MotionBox
        alignItems="center"
        as="nav"
        bottom={{ base: 0, tablet: undefined }}
        color={mode === 'dark' ? 'neutral.900' : 'neutral.0'}
        height={{
          base: `${mobileNavbarHeight}px`,
          tablet: `${desktopNavbarHeight}px`,
        }}
        justifyContent="space-between"
        minW="100%"
        paddingX={{ base: 0, tablet: 800 }}
        position={{ base: 'fixed', tablet: isSticky ? 'sticky' : 'relative' }}
        style={isSticky ? style : undefined}
        sx={{
          ...belowTablet(mobileNavbarStyle),
          ...hideInFullscreen,
        }}
        top={{ base: undefined, tablet: 0 }}
        zIndex={zIndex}
        {...props}
        ref={ref}
      >
        <Logo color="inherit" display={{ base: 'none', tablet: 'block' }} />
        <SimpleGrid
          color="inherit"
          columns={navbarItems.length}
          gap={{ base: 100, tablet: 700 }}
          height={{
            base: `${mobileNavbarHeight}px`,
            tablet: `${desktopNavbarHeight}px`,
          }}
          paddingInline={{ base: undefined, tablet: 900 }}
          paddingX={{ base: 100, tablet: undefined }}
          width={{ base: loggedIn ? '100%' : '100vw', tablet: 'auto' }}
        >
          {navbarItems.map(item => (
            <NavbarButton
              isSelected={item.path === selectedPath}
              key={item.name}
              mode={mode}
              navbarItem={item}
              onClick={() => handleClick(item)}
              padding={0}
            />
          ))}
        </SimpleGrid>
        <Box display={{ base: 'none', tablet: 'flex' }} justifyContent="center">
          {loggedIn ? (
            <IconButton
              icon={iUser}
              label={t('navbar.profile')}
              onClick={() => setCurrentMenu(HomeownerAppMenuNames.PROFILE)}
              size="medium"
              variant="ghost"
              {...nudge(300, 'right')}
            />
          ) : (
            <Button
              colorScheme={mode}
              onClick={() => setCurrentMenu(HomeownerAppMenuNames.SIGN_IN)}
              optionalTrackingProperties={{
                ...trackingData,
                track_id: 'desktop-landing-page-login-button',
                button_location: 'top-of-page',
              }}
              variant="ghost"
              {...nudge(500, 'right')}
            >
              {t('navbar.logIn')}
            </Button>
          )}
        </Box>
      </MotionBox>
    );
  },
);

export { Navbar };
